import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import { cn } from "../lib/utils";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  variant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link"
    | "primary"
    | "success";
  size?: "default" | "sm" | "lg" | "icon";
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size = "default", asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          "inline-flex items-center justify-center rounded-lg text-sm font-semibold ring-offset-white transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-gray-950 dark:focus-visible:ring-orange-400",
          {
            "bg-gray-900 text-gray-50 hover:bg-gray-800 shadow-lg hover:shadow-xl dark:bg-gray-50 dark:text-gray-900 dark:hover:bg-gray-200":
              variant === "default",
            "bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-lg hover:shadow-xl hover:shadow-red-500/30 border-2 border-red-400/40":
              variant === "destructive",
            "border-2 border-gray-600/50 bg-transparent hover:bg-gray-800/50 hover:border-orange-500/40 text-gray-200 hover:text-white shadow-lg backdrop-blur-sm":
              variant === "outline",
            "bg-gray-800/80 text-gray-200 hover:bg-gray-700/80 hover:text-white shadow-lg border-2 border-gray-700/50":
              variant === "secondary",
            "hover:bg-gray-800/50 hover:text-white text-gray-300": variant === "ghost",
            "text-orange-400 underline-offset-4 hover:underline hover:text-orange-300":
              variant === "link",
            "bg-gradient-to-r from-orange-500 via-orange-600 to-pink-500 hover:from-orange-600 hover:via-orange-700 hover:to-pink-600 text-white font-bold shadow-xl hover:shadow-2xl shadow-orange-500/40 hover:shadow-orange-500/50 border-2 border-orange-400/40":
              variant === "primary",
            "bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-500 hover:from-emerald-600 hover:via-emerald-700 hover:to-teal-600 text-white font-bold shadow-xl hover:shadow-2xl shadow-emerald-500/40 hover:shadow-emerald-500/50 border-2 border-emerald-400/40":
              variant === "success",
          },
          {
            "h-10 px-4 py-2": size === "default",
            "h-9 rounded-md px-3": size === "sm",
            "h-11 rounded-md px-8": size === "lg",
            "h-10 w-10": size === "icon",
          },
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button };
